"use client";

import { useEffect, useState } from "react";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { BadgeChip, formatNewJobsBadgeLabel } from "./new-jobs-badge-chip";

const GUEST_LAST_SEEN_KEY = "lokerdollar:guest-last-feed-seen";

type Props = {
	locale: Locale;
	t: Dictionary;
};

/**
 * Guest counterpart to {@link NewJobsBadge}. Renders only the chip — the
 * navbar's wordmark Link wraps it.
 */
export function NewJobsBadgeGuest({ locale, t }: Props) {
	const [count, setCount] = useState<number>(0);
	const [ready, setReady] = useState(false);

	useEffect(() => {
		let cancelled = false;
		let since: string | null = null;
		try {
			since = localStorage.getItem(GUEST_LAST_SEEN_KEY);
		} catch {
			// blocked — behave as first-time guest.
		}
		if (!since) {
			setReady(true);
			return;
		}

		const sinceUnix = Math.floor(new Date(since).getTime() / 1000);
		fetch(`/api/jobs/new-count?since=${sinceUnix}`, { method: "GET" })
			.then((r) =>
				r.ok ? (r.json() as Promise<{ count?: number }>) : { count: 0 },
			)
			.then((data) => {
				if (cancelled) return;
				setCount(typeof data.count === "number" ? data.count : 0);
				setReady(true);
			})
			.catch(() => {
				if (!cancelled) setReady(true);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	if (!ready || count <= 0) return null;

	const numDisplay = count > 99 ? "99+" : String(count);
	const aria =
		locale === "en"
			? `${numDisplay} new jobs since your last visit`
			: `${numDisplay} lowongan baru sejak kunjungan terakhirmu`;
	return (
		<BadgeChip
			label={formatNewJobsBadgeLabel(count, t, locale)}
			aria={aria}
			href={`/${locale}/jobs?posted=24h#feed`}
			count={count}
		/>
	);
}
